import { Link } from "react-router-dom";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Camera, MapPin, Route as RouteIcon, X, Plus } from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { Reveal } from "@/components/motion";

const SITE = "https://cphhighlights.com";
const mapsUrl = (name) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, Copenhagen`)}`;

// Streets that photograph beautifully — real photos where we have them, honest
// typographic cards where we don't (yet — send us yours!)
const STREETS = [
  {
    name: "Magstræde",
    area: "Indre By",
    areaId: "indre-by",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/wv77dc9y_2026-09-28%2000_32_12.543%2B0200.webp",
    text: "The postcard street: cobbles, crooked 18th-century houses in ice-cream colours and barely a straight line in sight.",
    tip: "Come before 9am — the street is empty and the light lands softly on the facades.",
  },
  {
    name: "Nyboder",
    area: "City centre east",
    areaId: "indre-by",
    img: "/streets/nyboder.webp",
    text: "Rows of ochre-yellow naval terraces built in the 1600s for sailors' families — hundreds of identical little houses, still lived in.",
    tip: "Golden hour makes the yellow rows glow.",
  },
  {
    name: "Gråbrødretorv",
    area: "Indre By",
    areaId: "indre-by",
    img: "/streets/grabrodretorv.webp",
    text: "A plane-tree square ringed by pastel townhouses rebuilt after the great fire of 1728 — more outdoor café than street, and all the better for it.",
    tip: "Midday, when the square is full of life.",
  },
  {
    name: "Overgaden Neden Vandet",
    area: "Christianshavn",
    areaId: "christianshavn",
    img: "/planner/056967bb7024.webp",
    text: "The lane along Christianshavns Kanal — old warehouses, houseboats and little bridges, with the Church of Our Saviour's spiral towering overhead.",
    tip: "Golden hour, when the warehouses reflect in the canal.",
  },
  {
    name: "Sofiegade",
    area: "Christianshavn",
    areaId: "christianshavn",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/z6es680k_20260926_175755.webp",
    text: "Honest truth: the star here is one single facade — the cheerfully hand-painted, stripey house at no. 4. The lane around it is quiet and sweet, but you come for that one wall.",
    tip: "Don't plan a whole stop — swing by for two minutes on your way along the canals.",
  },
  {
    name: "Amagergade",
    area: "Christianshavn",
    areaId: "christianshavn",
    img: "/streets/amagergade.webp",
    text: "Crooked old houses leaning over the cobbles — Christianshavn at its most storybook.",
  },
  {
    name: "Krusemyntegade",
    area: "Indre By",
    areaId: "indre-by",
    img: "/streets/krusemyntegade.webp",
    text: "A short rainbow of small rowhouses just outside Kastellet's ramparts.",
    tip: "Pair it with the walk around the star fortress.",
  },
  {
    name: "Olufsgade",
    area: "Østerbro",
    areaId: "osterbro",
    img: "/streets/olufsgade.webp",
    text: "A pocket of old working-class Østerbro — modest, colourful and quietly pretty.",
  },
];

// Ready-made walking route through all eight streets — a sensible on-foot order:
// old Østerbro first, past Kastellet and Nyboder into the cobbled centre,
// then over the water to end in Christianshavn. ~7 km, about 2 hours.
const ROUTE_STOPS = [
  "Olufsgade",
  "Krusemyntegade",
  "Nyboder",
  "Gråbrødretorv",
  "Magstræde",
  "Overgaden Neden Vandet",
  "Sofiegade",
  "Amagergade",
];
const cph = (s) => encodeURIComponent(`${s}, Copenhagen`);
const photoRouteUrl = (stops) =>
  `https://www.google.com/maps/dir/?api=1&origin=${cph(stops[0])}&destination=${cph(stops[stops.length - 1])}&waypoints=${stops.slice(1, -1).map(cph).join("%7C")}&travelmode=walking`;

const ITEM_LIST = {  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "The most photo-worthy streets in Copenhagen",
  description:
    "Eight photo-worthy Copenhagen streets with local photo tips — also available as a ready-made 7 km walking route (Olufsgade → Christianshavn) that links them all.",
  itemListElement: STREETS.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${s.name}, ${s.area}, Copenhagen`,
    url: `${SITE}/photo-worthy-streets`,
  })),
};

export default function PhotoStreetsPage() {
  const [skipped, setSkipped] = useState([]);
  const routeStops = ROUTE_STOPS.filter((s) => !skipped.includes(s));
  return (
    <main data-testid="photo-streets-page" className="bg-[#F9F8F6] text-black min-h-screen">
      <Seo
        title="The Most Photo-Worthy Streets in Copenhagen"
        description="Copenhagen's prettiest streets, picked by a local — with photo tips for each and a ready-made 7 km walking route that links all eight."
        path="/photo-worthy-streets"
        image={`${SITE}/streets/amagergade.webp`}
      />
      <PageSchema
        name="The Most Photo-Worthy Streets in Copenhagen"
        description="Copenhagen's prettiest streets, picked by a local — with photo tips for each and a ready-made 7 km walking route that links all eight."
        path="/photo-worthy-streets"
        crumb="Photo-worthy streets"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ITEM_LIST)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-white">
        <div className="absolute inset-0">
          <img src={"https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/wv77dc9y_2026-09-28%2000_32_12.543%2B0200.webp"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-12 md:pb-24">
          <div className="flex items-center gap-3 mb-10 md:mb-12">
            <Link
              to="/"
              data-testid="photo-streets-back"
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm px-3.5 py-2 rounded-full bg-white border border-zinc-200 hover:bg-zinc-50 transition-colors"
            >
              <ArrowLeft size={14} /> Back to start
            </Link>
            <ShareButton
              url="/photo-worthy-streets"
              title="The Most Photo-Worthy Streets in Copenhagen"
              data-testid="photo-streets-share"
              className="bg-white text-black border-zinc-200 hover:bg-zinc-50"
            />
          </div>
          <Reveal>
            <p className="font-body font-800 text-xs md:text-sm uppercase tracking-[0.35em] text-white mb-5">
              STREETS WORTH THE DETOUR
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-7xl">
              Photo worthy streets
              <br />
              <span className="font-editorial italic font-600">in Copenhagen</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-base md:text-lg text-white/70 mt-6 max-w-xl leading-relaxed">
              Cobbles, crooked houses and canals — the Copenhagen streets your
              camera will thank you for, picked by someone who walks them daily.
            </p>
          </Reveal>
        </div>
      </section>

      {/* streets */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STREETS.map((s, i) => (
            <Reveal key={s.name} y={30} delay={Math.min((i % 2) * 0.06, 0.12)}>
              <article
                data-testid={`street-card-${i}`}
                className="h-full bg-white border border-zinc-200 shadow-brut rounded-2xl overflow-hidden flex flex-col"
              >
                {s.img ? (
                  <img
                    src={s.img}
                    alt={`${s.name}, ${s.area}, Copenhagen`}
                    loading="lazy"
                    className="w-full h-56 md:h-72 object-cover"
                  />
                ) : (
                  <div className="w-full h-56 md:h-72 bg-[#13141A] text-white flex flex-col items-start justify-end p-6">
                    <span className="font-display font-900 text-6xl text-[#DF3226] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-900 text-2xl mt-2">{s.name}</span>
                    <span className="font-editorial italic text-sm text-white/60 mt-2">
                      Photo coming soon — this one is worth shooting yourself.
                    </span>
                  </div>
                )}
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <a
                      href={mapsUrl(s.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`street-maps-${i}`}
                      className="font-display font-900 tracking-tight text-2xl md:text-3xl hover:text-[#DF3226] hover:underline underline-offset-4 transition-colors"
                    >
                      {s.name}
                    </a>
                    <Link
                      to={`/areas/${s.areaId}`}
                      data-testid={`street-area-${i}`}
                      className="shrink-0 inline-flex items-center gap-1 font-body font-800 text-[11px] tracking-widest uppercase text-[#DF3226] hover:text-black transition-colors"
                    >
                      <MapPin size={12} /> {s.area}
                    </Link>
                  </div>
                  <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 flex-1">{s.text}</p>
                  {s.tip && (
                    <p className="font-editorial italic text-sm text-black/55 mt-4 flex items-start gap-1.5">
                      <Camera size={14} className="shrink-0 mt-0.5" /> {s.tip}
                    </p>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* ready-made walking route */}
        <Reveal y={30}>
          <div
            data-testid="photo-walk-route"
            className="mt-14 md:mt-20 bg-white border border-zinc-200 shadow-brut rounded-2xl p-8 md:p-12"
          >
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
              ONE WALK · ALL EIGHT STREETS
            </p>
            <div className="flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="flex-1">
                <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
                  The photo walk,{" "}
                  <span className="font-editorial italic font-600">ready-made</span>
                </h2>
                <p className="font-body text-sm md:text-base text-black/60 mt-3 max-w-lg">
                  About 7 km, roughly two hours with photo stops — old Østerbro to Christianshavn,
                  over the water at the end. Opens in Google Maps.
                </p>
                <p className="font-body text-xs text-black/40 mt-2">
                  Tap ✕ on any stop to drop it — the Maps route updates.
                </p>
                <ol className="flex flex-wrap gap-2 mt-5">
                  {routeStops.map((s, i) => (
                    <li
                      key={s}
                      data-testid={`photo-walk-stop-${i}`}
                      className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-[#F9F8F6] border border-zinc-200 rounded-full pl-3 pr-2 py-1.5"
                    >
                      <span className="font-display font-900 text-[#DF3226]">{i + 1}</span> {s}
                      <button
                        type="button"
                        data-testid={`photo-walk-remove-${i}`}
                        aria-label={`Remove ${s} from the route`}
                        onClick={() => setSkipped((p) => [...p, s])}
                        className="ml-0.5 rounded-full p-0.5 text-black/30 hover:text-[#DF3226] hover:bg-black/5 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ol>
                {skipped.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="font-body text-xs text-black/40">Off the route:</span>
                    {ROUTE_STOPS.filter((s) => skipped.includes(s)).map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`photo-walk-readd-${s.replace(/\s+/g, "-")}`}
                        onClick={() => setSkipped((p) => p.filter((n) => n !== s))}
                        className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/40 border border-dashed border-zinc-300 rounded-full px-3 py-1.5 hover:text-black hover:border-zinc-400 transition-colors"
                      >
                        <Plus size={11} /> {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {routeStops.length >= 2 && (
                <a
                  href={photoRouteUrl(routeStops)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="photo-walk-open-btn"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#DF3226] text-white font-body font-800 text-sm px-5 py-3 rounded-full hover:bg-white transition-colors"
                >
                  <MapPin size={16} /> Open the whole route in Google Maps
                </a>
              )}
            </div>
          </div>
        </Reveal>

      </section>

      {/* planner CTA — full-width band, same style as the other pages */}
      <section data-testid="photo-streets-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Walked the pretty streets?
            <br />
            <span className="font-editorial italic font-600">The planner sorts the rest</span>
          </h2>
          <Link
            to="/planner"
            data-testid="photo-streets-planner-cta"
            className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      <GygTourBand tourId="1112342" label="WALK THEM WITH A LOCAL" />
      <ExploreLinks
        testid="explore-streets"
        links={[
          { label: "More to point the camera at — things to do in Copenhagen", to: "/things-to-do" },
          { label: "The ready-made 3 days — Copenhagen itinerary", to: "/copenhagen-itinerary" },
          { label: "Every street's neighbourhood — the area guides", to: "/areas" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
